/**
 * @fileoverview The pieces a gardener's public pages share: the garden page (/@amy) and
 * its plant and bed pages (/@amy/cherokee-purple, /@amy/bed-a).
 *
 * - PublicBar: the slim top bar of every public page (logo to the landing page,
 *   Gardens on Grow, Start your garden / Back to my garden).
 * - LargePicture: a share picture or photo, large, its place held at its shape.
 * - GardenInvite: "Keep your own garden this way" and "Start a garden" (on the
 *   garden page, given the gardener's name: "Grow your own like Amy" in a
 *   green card with "Start your garden free"),
 *   carrying the garden's code as the sign-up's credit (?ref), as a share
 *   page's invitation carries its share's (lib/gardenPage useGardenCredit
 *   keeps it on arrival).
 *
 * Images are lazy-loaded at fixed sizes and aspect ratios, so nothing jumps
 * as they arrive.
 *
 * The garden page's own sections (hero, chips, latest share, bed and plant
 * cards) are in gardenPageSections.
 *
 * Where: GardenPage and GardenItemPage.
 * API: none.
 * Settings: SHARE_FRAME_SIZES (config/tunables/plantList), SHARE_PICTURE_MAX_HEIGHT
 * (config/tunables/shared).
 * Words (en/es): publicBar.*, gardensPage.title, gardenPage.page.titleOf, .title, .noCover,
 * .inviteBody, .inviteTitleOf, .inviteBodyNew, .inviteStart;
 * profile.links.website; share.page.inviteTitle, .inviteAction.
 *
 * @module gardenPageParts
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sprout } from 'lucide-react';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { PublicHeader } from '@/shared/components/layout/PublicHeader';
import { apiSrc } from './lib/gardenPage';
import { SHARE_FRAME_SIZES } from '@/config/tunables/plantList';
import { SHARE_PICTURE_MAX_HEIGHT } from '@/config/tunables/shared';

/**
 * The bar across the top of every public page (garden, plant, bed, share and
 * Gardens on Grow): the one public header (PublicHeader), the same as the
 * landing page's (owner, 2026-10-15: "the pages - landing, public garden
 * pages all need a unified header"): "Grow by Garden Gameplan" to the landing
 * page, Gardens, and Sign in with Start your garden for a visitor, or Back to
 * my garden when signed in. It is the only navigation a public page has.
 */
export const PublicBar: React.FC = () => <PublicHeader />;

/**
 * The page's frame: the public top bar (PublicBar) above, the column
 * phone-wide, always in Grow's own cream and green (the `grow` theme class on
 * its root, as /gardens has it) whatever theme the browser last kept, so a
 * first-time visitor never sees the app's navy and white. Bare, it is only the
 * page's ground under the bar: the garden page draws its own column and
 * full-width top (gardenPageSections).
 *
 * Moving from one public page to another (a plant to the next, a card to its
 * page, "See the whole garden") starts the new page at its top, as a browser
 * does for a new page; an address with a #fragment (#checkups) is left to
 * scroll itself there.
 *
 * @param props.children - The page.
 * @param props.bare - Draw no column.
 */
export const GardenFrame: React.FC<{ children: React.ReactNode; bare?: boolean }> = ({ children, bare = false }) => {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
    // Only a new page moves to the top, not a new #fragment on the same one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return (
    <div className="grow min-h-screen overflow-x-clip bg-background text-foreground">
      <PublicBar />
      {bare ? children : <div className="mx-auto w-full max-w-lg px-4 py-10">{children}</div>}
    </div>
  );
};

/** What a large picture is told: its API path, what it is, and its shape. */
interface LargePictureProps {
  /** The picture's API path ("/api/…") or full address. */
  src: string;
  /** What it is of, for a screen reader. */
  alt: string;
  /** A share's composed picture (its shape by size), or a photo (held in a 4:3 box). */
  kind: 'frame' | 'photo';
  /** A share picture's shape: square, story or pin. */
  size?: string | null;
}

/**
 * A share picture or a photo, large: its place held at its shape before it
 * arrives (a share picture's own, a photo's 4:3 box), capped in height like
 * the share page's pictures, so nothing below it jumps.
 *
 * @param props - LargePictureProps.
 */
export const LargePicture: React.FC<LargePictureProps> = ({ src, alt, kind, size }) => {
  const shape = kind === 'frame' && size && size in SHARE_FRAME_SIZES
    ? SHARE_FRAME_SIZES[size as keyof typeof SHARE_FRAME_SIZES]
    : kind === 'frame' ? SHARE_FRAME_SIZES.square : { w: 4, h: 3 };
  return (
    <div className="flex justify-center">
      <div
        className="relative overflow-hidden rounded-sm bg-muted shadow-md data-[small=true]:bg-transparent data-[small=true]:shadow-none"
        style={{ aspectRatio: `${shape.w} / ${shape.h}`, width: `min(100%, calc(${SHARE_PICTURE_MAX_HEIGHT} * ${shape.w / shape.h}))` }}
      >
        <FramedPhoto src={apiSrc(src)} alt={alt} fit={kind === 'frame' ? 'cover' : 'contain'} loading="lazy" />
      </div>
    </div>
  );
};

/**
 * A bed's cover at 4:3, or a quiet placeholder of the same size.
 *
 * @param props.src - The cover's API path, or null.
 * @param props.name - The bed's name, for the placeholder's label.
 */
export const BedCover: React.FC<{ src: string | null; name: string }> = ({ src, name }) => {
  const { reactTranslate: t } = useTranslation();
  return src ? (
    <span className="relative block aspect-[4/3] w-full overflow-hidden bg-muted data-[small=true]:bg-transparent">
      <FramedPhoto src={apiSrc(src)} alt="" loading="lazy" />
    </span>
  ) : (
    <span className="flex aspect-[4/3] w-full items-center justify-center bg-muted" role="img" aria-label={`${name}: ${t('gardenPage.page.noCover')}`}>
      <Sprout className="h-8 w-8 text-muted-foreground/50" aria-hidden="true" />
    </span>
  );
};

/**
 * The invitation at the foot of every garden page: "Start a garden", the
 * garden's code going with it as the sign-up's credit (?ref). Given the
 * gardener's name it is the garden page's green card, "Grow your own like
 * Amy" with "Start your garden free" (the link is the same).
 *
 * @param props.code - The garden's code; without one the link is /?start=1.
 * @param props.name - The gardener's name as shown, for "Grow your own like Amy"; given, the green card.
 * @param props.card - Draw the green card without a name too ("Grow your own garden"); the garden page asks for it.
 */
export const GardenInvite: React.FC<{ code: string | null | undefined; name?: string | null; card?: boolean }> = ({ code, name, card = false }) => {
  const { reactTranslate: t } = useTranslation();
  const to = code ? `/?ref=${encodeURIComponent(code)}` : '/?start=1';
  if (card || name) {
    return (
      <div className="rounded-2xl bg-primary p-5 text-primary-foreground">
        <p className="font-grow text-xl font-bold">{name ? t('gardenPage.page.inviteTitleOf', { name: name.replace(/^@/, '') }) : t('share.page.inviteTitle')}</p>
        <p className="mt-1 text-sm text-primary-foreground/90">{t('gardenPage.page.inviteBodyNew')}</p>
        <Link
          to={to}
          className="mt-4 flex h-12 w-full items-center justify-center rounded-xl bg-background font-grow text-base font-bold text-primary hover:bg-background/90"
        >
          {t('gardenPage.page.inviteStart')}
        </Link>
      </div>
    );
  }
  return (
    <div className="mt-12 rounded-sm border bg-card p-5">
      <p className="font-grow text-lg font-bold">{t('share.page.inviteTitle')}</p>
      <p className="mt-1 text-sm text-muted-foreground">{t('gardenPage.page.inviteBody')}</p>
      <Link
        to={to}
        className="mt-4 inline-flex items-center rounded-sm bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        {t('share.page.inviteAction')}
      </Link>
    </div>
  );
};
